import { useState, type ReactElement } from 'react';
import { App, Button, Form, Input, Modal, Popconfirm } from 'antd';
import { useTranslation } from '@govard/i18n-core/react';
import { useDealStageMutation } from '../hooks/useDealStageMutation.js';
import { STAGE_ORDER } from '../lib/kanban.js';
import { getEnumLabel } from '../../../shared/format/enum-labels.js';
import type { DealStage } from '../api/deals.client.js';

export function DealStageStepper({
  dealId,
  stage,
}: {
  dealId: string;
  stage: DealStage;
}): ReactElement {
  const { t } = useTranslation();
  const { notification, message } = App.useApp();
  const mutation = useDealStageMutation();
  const [lostOpen, setLostOpen] = useState(false);
  const [form] = Form.useForm<{ reason: string }>();
  const move = async (toStage: DealStage, reason?: string): Promise<void> => {
    const previous = stage;
    try {
      await mutation.mutateAsync({ dealId, toStage, ...(reason ? { reason } : {}) });
      setLostOpen(false);
      notification.success({
        key: `stage:${dealId}`,
        title: t('crm.deal_detail.stage_changed', { stage: getEnumLabel('dealStage', toStage, t) }),
        duration: 8,
        actions: (
          <Button
            onClick={() => {
              notification.destroy(`stage:${dealId}`);
              void mutation
                .mutateAsync({ dealId, toStage: previous })
                .catch(() => message.error(t('crm.entity_detail.save_error')));
            }}
          >
            {t('crm.entity_detail.undo')}
          </Button>
        ),
      });
    } catch {
      void message.error(t('crm.entity_detail.save_error'));
    }
  };
  return (
    <>
      <nav className="deal-stage-stepper" aria-label={t('crm.deal_detail.pipeline')}>
        {STAGE_ORDER.map((value, index) => {
          const current = value === stage;
          const button = (
            <Button
              data-testid={`deal-stage-${value}`}
              aria-current={current ? 'step' : undefined}
              className={`deal-stage-button${current ? ' is-current' : index < STAGE_ORDER.indexOf(stage) && value !== 'Lost' ? ' is-past' : ''}${value === 'Won' ? ' is-won' : value === 'Lost' ? ' is-lost' : ''}`}
              disabled={current || mutation.isPending}
              onClick={
                value === 'Lost'
                  ? () => {
                      form.resetFields();
                      setLostOpen(true);
                    }
                  : undefined
              }
            >
              {getEnumLabel('dealStage', value, t)}
            </Button>
          );
          return value === 'Lost' ? (
            <span key={value}>{button}</span>
          ) : (
            <Popconfirm
              key={value}
              title={t('crm.deal_detail.move_confirm', {
                stage: getEnumLabel('dealStage', value, t),
              })}
              okText={t('crm.entity_detail.confirm')}
              cancelText={t('crm.entity_detail.cancel')}
              onConfirm={() => move(value)}
              disabled={current || mutation.isPending}
            >
              {button}
            </Popconfirm>
          );
        })}
      </nav>
      <Modal
        title={t('crm.deal_detail.loss_reason')}
        open={lostOpen}
        onCancel={() => setLostOpen(false)}
        onOk={() => form.submit()}
        okText={getEnumLabel('dealStage', 'Lost', t)}
        cancelText={t('crm.entity_detail.cancel')}
        confirmLoading={mutation.isPending}
        destroyOnHidden
        closable={{ 'aria-label': t('crm.deal_card.close_chat') }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={({ reason }) => void move('Lost', reason?.trim())}
        >
          <Form.Item name="reason" label={t('crm.deal_detail.loss_reason')}>
            <Input.TextArea data-testid="deal-loss-reason" maxLength={200} rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
